// WorkOrderModals.tsx — Sprint 11. Five modals as named exports:
// WorkOrderEditModal, CompleteOrderModal, AssignVendorModal,
// UpdateCostsModal, SignOffModal. Each is stateless: open flag +
// close/save callbacks + optional row being acted on. Form
// instances stay in the orchestrator (next to the state that
// drives them) and are passed in as props — same pattern as
// ParkingModals.tsx.
import React from 'react';
import {
  Button, Col, Form, FormInstance, Input, InputNumber, Modal, Row, Select, Space, Typography, Divider, DatePicker,
} from 'antd';
import {
  CheckCircleOutlined, DollarOutlined, SafetyCertificateOutlined, UserOutlined,
} from '@ant-design/icons';
import type { WorkOrder } from './workOrderTypes';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

// ─── 1. Create / Edit Work Order ─────────────────────────────────────────────

export interface WorkOrderEditModalProps {
  open:         boolean;
  editingOrder: WorkOrder | null;
  form:         FormInstance;
  onCancel:     () => void;
  onSubmit:     (values: any) => void;
}

export const WorkOrderEditModal: React.FC<WorkOrderEditModalProps> = ({
  open, editingOrder, form, onCancel, onSubmit,
}) => (
  <Modal
    title={editingOrder ? 'Edit Work Order' : 'New Work Order'}
    open={open}
    onCancel={onCancel}
    footer={null}
    width={700}
  >
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Please enter a title' }]}>
        <Input placeholder="e.g. HVAC Repair — Unit 4B" />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <TextArea rows={3} placeholder="Describe the work to be done..." />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="unitNumber" label="Unit Number">
            <Input placeholder="e.g. 4B" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="priority" label="Priority" initialValue="MEDIUM">
            <Select>
              <Option value="LOW">Low</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="HIGH">High</Option>
              <Option value="URGENT">Urgent</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider plain style={{ fontSize: 13, color: '#718096' }}>Vendor</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="vendorName" label="Vendor Name">
            <Input placeholder="e.g. ABC Plumbing" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="vendorContact" label="Vendor Contact">
            <Input placeholder="Phone or email" />
          </Form.Item>
        </Col>
      </Row>

      <Divider plain style={{ fontSize: 13, color: '#718096' }}>Schedule & Cost</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="scheduledDate" label="Scheduled Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="estimatedCost" label="Estimated Cost ($)">
            <InputNumber style={{ width: '100%' }} min={0} step={50} prefix="$" placeholder="0.00" />
          </Form.Item>
        </Col>
      </Row>

      {editingOrder && (
        <Form.Item name="status" label="Status">
          <Select>
            <Option value="OPEN">Open</Option>
            <Option value="IN_PROGRESS">In Progress</Option>
            <Option value="COMPLETED">Completed</Option>
            <Option value="CANCELLED">Cancelled</Option>
          </Select>
        </Form.Item>
      )}

      <Form.Item name="notes" label="Notes">
        <TextArea rows={2} placeholder="Additional notes..." />
      </Form.Item>

      <Form.Item className="mb-0">
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit">
            {editingOrder ? 'Save Changes' : 'Create Work Order'}
          </Button>
        </div>
      </Form.Item>
    </Form>
  </Modal>
);

// ─── 2. Complete Work Order ──────────────────────────────────────────────────

export interface CompleteOrderModalProps {
  open:            boolean;
  completingOrder: WorkOrder | null;
  form:            FormInstance;
  onCancel:        () => void;
  onSubmit:        (values: any) => void;
}

export const CompleteOrderModal: React.FC<CompleteOrderModalProps> = ({
  open, completingOrder, form, onCancel, onSubmit,
}) => (
  <Modal
    title={
      <Space>
        <CheckCircleOutlined style={{ color: '#38a169' }} />
        <span>Complete Work Order</span>
      </Space>
    }
    open={open}
    onCancel={onCancel}
    footer={null}
    width={480}
  >
    {completingOrder && (
      <>
        <div style={{
          padding: '12px 16px', background: '#f0fff4', borderRadius: 8,
          border: '1px solid #9ae6b4', marginBottom: 20,
        }}>
          <Text strong>{completingOrder.title}</Text>
          {completingOrder.vendorName && (
            <div>
              <Text type="secondary" style={{ fontSize: 13 }}>Vendor: {completingOrder.vendorName}</Text>
            </div>
          )}
          {completingOrder.estimatedCost != null && (
            <div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Estimated cost: ${completingOrder.estimatedCost.toLocaleString()}
              </Text>
            </div>
          )}
        </div>
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="actualCost"
            label="Actual Cost ($)"
            rules={[{ required: true, message: 'Please enter the actual cost' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0} step={50} prefix="$"
              placeholder="Enter final invoice amount"
            />
          </Form.Item>
          <Form.Item className="mb-0">
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button onClick={onCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" icon={<CheckCircleOutlined />}>
                Confirm Completion
              </Button>
            </div>
          </Form.Item>
        </Form>
      </>
    )}
  </Modal>
);

// ─── 3. Assign Vendor ────────────────────────────────────────────────────────

export interface AssignVendorModalProps {
  open:     boolean;
  form:     FormInstance;
  vendors:  any[];
  onCancel: () => void;
  onOk:     () => void;
}

export const AssignVendorModal: React.FC<AssignVendorModalProps> = ({
  open, form, vendors, onCancel, onOk,
}) => (
  <Modal
    title={<Space><UserOutlined /><span>Assign Vendor</span></Space>}
    open={open}
    onOk={onOk}
    onCancel={onCancel}
    okText="Assign"
    width={460}
    destroyOnHidden
  >
    <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
      {vendors && vendors.length > 0 && (
        <Form.Item name="vendorId" label="Select from Vendors">
          <Select allowClear placeholder="Choose a registered vendor…">
            {vendors.map((v: any) => (
              <Option key={v.id} value={v.id}>{v.name}</Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item name="vendorName" label="Vendor Name">
        <Input placeholder="e.g. ABC Plumbing" />
      </Form.Item>
      <Form.Item name="vendorContact" label="Contact (phone / email)">
        <Input placeholder="e.g. 555-1234" />
      </Form.Item>
    </Form>
  </Modal>
);

// ─── 4. Update Costs ─────────────────────────────────────────────────────────

export interface UpdateCostsModalProps {
  open:     boolean;
  form:     FormInstance;
  onCancel: () => void;
  onOk:     () => void;
}

export const UpdateCostsModal: React.FC<UpdateCostsModalProps> = ({
  open, form, onCancel, onOk,
}) => (
  <Modal
    title={<Space><DollarOutlined /><span>Update Costs</span></Space>}
    open={open}
    onOk={onOk}
    onCancel={onCancel}
    okText="Save Costs"
    width={460}
    destroyOnHidden
  >
    <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="laborCost" label="Labor Cost ($)">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="materialCost" label="Material Cost ($)">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="invoiceNumber" label="Invoice Number">
        <Input placeholder="e.g. INV-2024-001" />
      </Form.Item>
    </Form>
  </Modal>
);

// ─── 5. Sign-Off ─────────────────────────────────────────────────────────────

export interface SignOffModalProps {
  open:          boolean;
  signingOrder:  WorkOrder | null;
  form:          FormInstance;
  onCancel:      () => void;
  onOk:          () => void;
}

export const SignOffModal: React.FC<SignOffModalProps> = ({
  open, signingOrder, form, onCancel, onOk,
}) => (
  <Modal
    title={<Space><SafetyCertificateOutlined /><span>Sign Off Work Order</span></Space>}
    open={open}
    onOk={onOk}
    onCancel={onCancel}
    okText="Sign Off"
    width={400}
    destroyOnHidden
  >
    {signingOrder && (
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <div style={{
          padding: '10px 14px', background: '#f0fff4', borderRadius: 8,
          border: '1px solid #9ae6b4', marginBottom: 16,
        }}>
          <Text strong>{signingOrder.title}</Text>
        </div>
        <Form.Item
          name="signedOffBy"
          label="Signed Off By"
          rules={[{ required: true, message: 'Please enter your name' }]}
        >
          <Input placeholder="e.g. Building Manager" />
        </Form.Item>
      </Form>
    )}
  </Modal>
);
